import React, { useEffect, useState } from 'react';
import { StyleSheet, TouchableOpacity, Image, View, Text, ScrollView } from 'react-native';
import { useIsFocused, useNavigation, useNavigationState } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import Urls from '../../services/Urls';
import ajax from '../../helpers/ajaxHelper';
import getPlatformType from '../../helpers/helpers';
import getInitials from '../../helpers/getInitials';
import { GradientWrapper, SelectedItem, ItemText } from './style';
import { saveUserProfile } from '../../redux/action/authActions';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from '../../navigation/RootNavigation';
import { COLORS, LAYOUT, BORDER_RADIUS } from '../../constants/theme';

var layoutType = getPlatformType();

// ─── Route helpers ─────────────────────────────────────────────────────────
const normalizeRouteKey = (routeName) => {
  if (routeName === 'Home') return 'home';
  return routeName;
};

const getActiveRouteName = (state) => {
  if (!state || !state.routes || typeof state.index !== 'number') return '';
  const route = state.routes[state.index];
  return route?.state ? getActiveRouteName(route.state) : (route?.name ?? '');
};

// ─── Menu configuration ────────────────────────────────────────────────────
const MENU_ITEMS = [
  {
    key: 'home',
    name: 'Dashboard',
    altKeys: ['home', 'Root', 'Dashboard', 'Home'],
    iconType: 'icon',
    iconName: 'home-heart',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'Properties',
    name: 'Properties',
    altKeys: ['Properties', 'PropertyDetails', 'PropertyInfo', 'PropertySection', 'PropertyAddress', 'Certification', 'Licensing', 'Requirements'],
    iconType: 'icon',
    iconName: 'home-plus',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'Tenants',
    name: 'Tenancies',
    altKeys: ['Tenants', 'TeanantOnboarding', 'TeanantInfo', 'CurrentOnboards', 'CompletedOnboards', 'ArchivedOnboards'],
    iconType: 'icon',
    iconName: 'home-group',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'MaintenanceProject',
    name: 'Maintenance',
    altKeys: ['MaintenanceProject', 'NewIssues', 'New_issues', 'ActiveProject', 'ActiveProjects', 'CompletedProject', 'CompletedProjects', 'ArchivedProject', 'ArchivedProjects'],
    iconType: 'icon',
    iconName: 'tools',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'Payments',
    name: 'Payments',
    altKeys: ['Payments', 'PaymentDetails'],
    iconType: 'icon',
    iconName: 'cash-multiple',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'Reminder',
    name: 'Reminders',
    altKeys: ['Reminder', 'AddReminder', 'EditReminder'],
    iconType: 'icon',
    iconName: 'alarm',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'MyDocs',
    name: 'Documents',
    altKeys: ['MyDocs', 'DocumentArchive'],
    iconType: 'icon',
    iconName: 'file-document-outline',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'MediaHub',
    name: 'Media Hub',
    altKeys: ['MediaHub', 'MediaHub Details', 'LandlordMediaDetails', 'MediaVideoDetails'],
    iconType: 'icon',
    iconName: 'play-box-outline',
    iconLib: 'MaterialCommunity'
  },
  {
    key: 'Setting',
    name: 'Settings',
    altKeys: ['Setting', 'AccountEdit', 'ChangePassword', 'MyAccount'],
    iconType: 'icon',
    iconName: 'settings-outline',
    iconLib: 'Ionicons'
  },
];

// ─── DrawerMenu Component ──────────────────────────────────────────────────
const DrawerMenu = (props) => {
  const navigation = useNavigation();
  const [user, setUser] = useState({});
  const focused = useIsFocused();
  const dispatch = useDispatch();
  const userToken = useSelector((state) => state?.AppReducer?.token);
  const selectedKey = normalizeRouteKey(
    useNavigationState((state) => getActiveRouteName(state))
  );

  const isItemSelected = (item) => {
    if (item.key === selectedKey) return true;
    if (item.altKeys && item.altKeys.includes(selectedKey)) return true;
    return false;
  };

  useEffect(() => {
    if (userToken) {
      ajax
        .get(Urls.PROFILE)
        .then(({ data }) => {
          if (data.status) {
            setUser(data?.data.userDetails);
            dispatch(saveUserProfile(data?.data.userDetails));
          }
        })
        .catch((error) => console.log('drawer profile error', error));
    }
  }, [focused, userToken]);

  const closeDrawer = () => {
    if (props?.setDrawer) props?.setDrawer(false);
  };

  const handleNavigation = (item) => {
    if (item.navigateParams) {
      navigate(item.key, item.navigateParams);
    } else {
      navigation.navigate(item.key);
    }
    closeDrawer();
  };

  const getFullName = () => {
    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    if (!fullName) return '';
    return fullName.length > 22 ? `${fullName.substring(0, 22)}...` : fullName;
  };

  const isVerified =
    user?.id_verification_status === 'completed' &&
    user?.kyc_verification_status === 'completed';

  const getInitialsLocal = () =>
    getInitials(`${user?.first_name || ''} ${user?.last_name || ''}`);

  const renderIcon = (item, selected) => {
    const tintColor = selected ? COLORS.sidebarActiveText : COLORS.sidebarIconInactive;

    if (item.iconType === 'image') {
      const source = selected && item.iconSourceActive ? item.iconSourceActive : item.iconSource;
      return (
        <Image
          style={[styles.menuIcon, layoutType === 'tab' && styles.menuIconTab]}
          resizeMode="contain"
          source={source}
          tintColor={tintColor}
        />
      );
    } else if (item.iconType === 'icon') {
      if (item.iconLib === 'MaterialCommunity') {
        return (
          <MaterialCommunityIcons
            name={item.iconName}
            size={20}
            color={tintColor}
          />
        );
      }
      return (
        <Ionicons
          name={item.iconName}
          size={20}
          color={tintColor}
        />
      );
    }
    return null;
  };

  const renderMenuItem = (item) => {
    const selected = isItemSelected(item);
    return (
      <TouchableOpacity
        key={item.key}
        onPress={() => handleNavigation(item)}
        activeOpacity={0.7}
      >
        <SelectedItem selected={selected}>
          <View style={styles.menuItemInner}>
            {renderIcon(item, selected)}
            {layoutType !== 'tab' && (
              <ItemText selected={selected}>{item.name}</ItemText>
            )}
          </View>
        </SelectedItem>
      </TouchableOpacity>
    );
  };

  const renderLogo = () => (
    <TouchableOpacity
      onPress={() => navigation.navigate('home')}
      style={styles.logoContainer}
      activeOpacity={0.8}
    >
      <Image
        source={require('../../assets/images/Logo/HubitaWeb.png')}
        style={layoutType === 'tab' ? styles.logoIconOnly : styles.logo}
        resizeMode="contain"
      />
    </TouchableOpacity>
  );

  const renderUserInfo = () => {
    if (layoutType === 'tab') {
      return (
        <View style={styles.userFooterTab}>
          <View style={styles.avatarCircleSmall}>
            <Text style={styles.avatarTextSmall}>{getInitialsLocal()}</Text>
          </View>
        </View>
      );
    }
    return (
      <View style={styles.userFooter}>
        <View style={styles.avatarCircle}>
          <Text style={styles.avatarText}>{getInitialsLocal()}</Text>
        </View>
        <View style={styles.userInfo}>
          <Text style={styles.userName} numberOfLines={1}>
            {getFullName() || 'User'}
          </Text>
          <View style={isVerified ? styles.verifiedBadge : styles.notVerifiedBadge}>
            <Text style={isVerified ? styles.verifiedText : styles.notVerifiedText}>
              {isVerified ? 'Verified' : ''}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <GradientWrapper>
      {renderLogo()}

      <ScrollView
        style={styles.menuList}
        showsVerticalScrollIndicator={false}
      >
        {MENU_ITEMS.map((item) => (
          <React.Fragment key={item.key}>
            {renderMenuItem(item)}
          </React.Fragment>
        ))}
      </ScrollView>

      {renderUserInfo()}
    </GradientWrapper>
  );
};

const styles = StyleSheet.create({
  logoContainer: {
    paddingHorizontal: layoutType === 'tab' ? 8 : 12,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderLight,
    alignItems: layoutType === 'tab' ? 'center' : 'flex-start',
  },
  logo: {
    height: 28,
    width: 120,
  },
  logoIconOnly: {
    height: 28,
    width: 40,
  },
  menuLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: COLORS.textMuted,
    letterSpacing: 1,
    paddingHorizontal: 12,
    marginTop: 20,
    marginBottom: 4,
  },
  menuList: {
    flex: 1,
    marginTop: layoutType === 'tab' ? 12 : 4,
  },
  menuItemInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuIcon: {
    height: 20,
    width: 20,
  },
  menuIconTab: {
    height: 22,
    width: 22,
  },
  userFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: COLORS.borderLight,
    marginTop: 8,
  },
  userFooterTab: {
    alignItems: 'center',
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: COLORS.borderLight,
    marginTop: 8,
  },
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarCircleSmall: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: COLORS.textWhite,
    fontSize: 13,
    fontWeight: '700',
  },
  avatarTextSmall: {
    color: COLORS.textWhite,
    fontSize: 12,
    fontWeight: '700',
  },
  userInfo: {
    marginLeft: 10,
    flex: 1,
  },
  userName: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textPrimary,
  },
  userRole: {
    fontSize: 11,
    color: COLORS.textSecondary,
    marginTop: 1,
  },
  verifiedBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#dcfce7',
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 4,
  },
  verifiedText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#16a34a',
  },
  notVerifiedBadge: {
    alignSelf: 'flex-start',
    backgroundColor: COLORS.primary,
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 4,
  },
  notVerifiedText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#d97706',
  },
});


export default DrawerMenu;
